import { ForgeMarkSpinner } from "~/components/branding/ForgeMarkSpinner";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

import { CloudRuntimeRouteBlockedPanel } from "./chat/CloudRuntimeRouteBlockedPanel";
import { ChatLoadingSkeleton } from "./chat/ChatLoadingSkeleton";
import {
  WORKSPACE_STARTUP_EXIT_MS,
  WORKSPACE_STARTUP_PHASE_MS,
  type WorkspaceStartupShellState,
} from "./workspaceStartupShell";

interface WorkspaceStartupStatusSurfaceProps {
  readonly isUnavailable: boolean;
  readonly statusVisible: boolean;
  readonly title: string;
}

function WorkspaceStartupStatusSurface({
  isUnavailable,
  statusVisible,
  title,
}: WorkspaceStartupStatusSurfaceProps) {
  const statusTitle = isUnavailable ? title : title ? `${title}…` : "Loading thread…";

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col transition-opacity ease-[cubic-bezier(0.25,1,0.5,1)]",
        statusVisible ? "opacity-100" : "opacity-0",
      )}
      data-workspace-startup-surface={isUnavailable ? "unavailable" : "busy"}
      data-workspace-thread-placeholder="true"
      style={{ transitionDuration: `${WORKSPACE_STARTUP_PHASE_MS}ms` }}
    >
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-4">
        <ForgeMarkSpinner
          className="size-5 shrink-0"
          markClassName="size-2.5"
          ringClassName={
            isUnavailable
              ? "border-border"
              : "border-border border-t-primary motion-reduce:animate-none"
          }
          variant={isUnavailable ? "idle" : "loading"}
        />
        <p className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">
          {statusTitle}
        </p>
        {isUnavailable ? (
          <Button
            className="h-7 border-border px-3 text-xs text-muted-foreground hover:text-foreground"
            size="sm"
            type="button"
            variant="outline"
            onClick={() => {
              window.location.reload();
            }}
          >
            Keep waiting
          </Button>
        ) : null}
      </div>

      <ChatLoadingSkeleton phase="workspace" />
    </div>
  );
}

interface WorkspaceStartupOverlayProps {
  readonly shell: WorkspaceStartupShellState;
}

export function WorkspaceStartupOverlay({ shell }: WorkspaceStartupOverlayProps) {
  const model = shell.overlayModel;

  if (!shell.isShellMounted || !model) {
    return null;
  }

  const isUnavailable = model.phase === "unavailable";
  // Render the same canonical route-block surface used by the thread route.
  // Guard defensively so a phase/state mismatch degrades to the spinner.
  const blockedState = model.phase === "runtime-blocked" ? shell.blockedState : null;
  const isBusy = !isUnavailable && !blockedState;

  return (
    <div
      aria-busy={isBusy}
      aria-hidden={shell.isExiting}
      aria-live="polite"
      className={cn(
        "absolute inset-0 z-30 flex bg-background transition-opacity ease-[cubic-bezier(0.25,1,0.5,1)]",
        shell.overlayVisible ? "opacity-100" : "pointer-events-none opacity-0",
      )}
      data-workspace-startup-exiting={shell.isExiting ? "true" : "false"}
      data-workspace-startup-phase={model.phase}
      role={isUnavailable ? "alert" : "status"}
      style={{ transitionDuration: `${WORKSPACE_STARTUP_EXIT_MS}ms` }}
    >
      {blockedState ? (
        <div
          className={cn(
            "m-auto flex w-full max-w-sm px-4 transition-opacity ease-[cubic-bezier(0.25,1,0.5,1)]",
            shell.statusVisible ? "opacity-100" : "opacity-0",
          )}
          data-workspace-startup-surface="blocked"
          style={{ transitionDuration: `${WORKSPACE_STARTUP_PHASE_MS}ms` }}
        >
          <CloudRuntimeRouteBlockedPanel blockedState={blockedState} />
        </div>
      ) : (
        <WorkspaceStartupStatusSurface
          isUnavailable={isUnavailable}
          statusVisible={shell.statusVisible}
          title={model.title}
        />
      )}
    </div>
  );
}
